import { useState, useMemo, useRef } from 'react';
import type { HTMLProps, MouseEventHandler } from 'react';
import interactionStyles from './index.module.less';
import type { Tools } from '../../../core/types';
import { Color, Type } from '../../../core/enums';
import Button from '../../archived/button';
import { Icon } from '../../atoms/icon';

type ObjectValues<T> = T[keyof T];
/**
 * The position of the toggle button
 */
export const TogglePosition = {
  TopLeft: 'TopLeft',
  TopRight: 'TopRight',
  BottomLeft: 'BottomLeft',
  BottomRight: 'BottomRight',
} as const;
export type TogglePosition = ObjectValues<typeof TogglePosition>;

/**
 * The position of the toggle button
 */
export const FlipDirection = {
  Horizontal: 'Horizontal',
  Vertical: 'Vertical',
} as const;
export type FlipDirection = ObjectValues<typeof FlipDirection>;

/**
 * Get the absolute positioning styles for the toggle button
 * @param position the position of the toggle button – `TopLeft | TopRight | BottomLeft | BottomRight`
 * @returns positioning styles
 */
const getToggleStyles = (position: TogglePosition) => {
  let toggleStyles;
  switch (position) {
    case TogglePosition.TopLeft:
      toggleStyles = {
        top: 15,
        left: 15,
      };
      break;
    case TogglePosition.TopRight:
      toggleStyles = {
        top: 15,
        right: 15,
      };
      break;
    case TogglePosition.BottomLeft:
      toggleStyles = {
        bottom: 15,
        left: 15,
      };
      break;
    case TogglePosition.BottomRight:
      toggleStyles = {
        bottom: 15,
        right: 15,
      };
      break;
  }
  return toggleStyles;
};

interface FlipStyles extends React.CSSProperties {
  MozTransform?: React.CSSProperties['transform'];
}

type FlipContentProps = {
  /**
   * z-Index for the flip content. Used to help solve context stack issues while using multiple flip states.
   * @default 'auto'
   */
  cardZIndex?: string;
  /**Extra css styling that can be applied to the container.
   * @default {}
   */
  containerStyle?: FlipStyles;
  /**
   * Custom container class name.
   * @default undefined
   */
  containerClassName?: string;
  frontClassName?: string;
  backClassName?: string;
  /**
   * False to show the front of the card, true to show the back
   */
  isFlipped: boolean;
  /**
   * Custom action passed to the button which handles the flip
   */
  handleFlip: MouseEventHandler<HTMLElement>;
  /**
   * Position of the toggle button.
   * @default TogglePosition.TopRight
   */
  togglePosition?: TogglePosition;
  /**
   * The speed of the flip animation when the card flips from back to front, the higher the number the slower the flip animation
   * @default 0.9
   */
  flipSpeedBackToFront?: number;
  /**
   * The speed of the flip animation when the card flips from front to back, the higher the number the slower the flip animation
   * @default 0.9
   */
  flipSpeedFrontToBack?: number;
  cardStyles?: { front?: FlipStyles; back?: FlipStyles };
  /**
   * False to rotate in opposite directions on both sides of the card, true to rotate in the same direction
   * @default false
   */
  isInfinite?: boolean;
  /**
   * If isInfinite, allow card to be set as rotated initially
   * @default 0
   */
  rotation?: number;

  /** Direction of the card flip (options are: 'Horizontal' or 'Vertical' )
   * @default FlipDirection.Horizontal ("Horizontal")
   */
  flipDirection?: FlipDirection;
  /** Color of the flip icon
   * @default Color.BrightBlue
   */
  iconColor?: Color;
  /** Hides the front icon
   * @default false
   */
  $hideIcons?: boolean;
  /** The aria label to be used on the front flipping button
   */
  ariaLabelFront: string;
  /** The aria label to be used on the back flipping button
   */
  ariaLabelBack: string;
  /** Two react elements, expressing the front and back of the FlipContent, respectively
   */
  children: [JSX.Element, JSX.Element];
  cardMaxHeight?: string;
  /** Tracking info to be passed to the interactive elements of the tile
   */
  tracking?: {
    frontIcon?: Tools;
    frontNoIcon?: Tools;
    backIcon?: Tools;
  };
} & HTMLProps<HTMLDivElement>;

/**
 * Custom hook to initialize flip state. This allows for tracking multiple flip content states,
 * and also for the ability for children or siblings to hook into the `isFlipped` and `setFlipped`
 * properties for custom functionality like only allowing a single card to be flipped open at a time, etc.
 * @returns isFlipped (true or false), setIsFlipped (allows manual rotation), rotation (the current rotation value of the card),
 * setRotation (function to dispatch state changes to rotation)
 */
export const useFlip = () => {
  const [isFlipped, setIsFlipped] = useState(false);
  const [rotation, setRotation] = useState(0);
  return { isFlipped, setIsFlipped, rotation, setRotation };
};

/**
 * A (mostly) styleless primitive that implements a card flipping animation.
 * Heavily inspired by: https://github.com/AaronCCWong/react-card-flip
 */
export const FlipContent = ({
  isFlipped,
  handleFlip,
  cardMaxHeight,
  cardStyles: { back: backStyles = {}, front: frontStyles = {} } = {},
  cardZIndex = 'auto',
  containerStyle = {
    width: '100%',
    height: '100%',
    maxHeight: cardMaxHeight ? cardMaxHeight : '460px',
  },
  containerClassName = '',
  frontClassName = '',
  backClassName = '',
  flipDirection = FlipDirection.Horizontal,
  flipSpeedFrontToBack = 0.9,
  flipSpeedBackToFront = 0.9,
  isInfinite = false,
  togglePosition = TogglePosition.TopRight,
  rotation = 0,
  iconColor = Color.BrightBlue,
  $hideIcons = false,
  ariaLabelFront,
  ariaLabelBack,
  tracking,
  children,
  ...rest
}: FlipContentProps & HTMLProps<HTMLDivElement>) => {
  const flipButton = useRef<HTMLButtonElement>(null);
  const backTile = useRef<HTMLDivElement>(null);
  const flipContent = useRef<HTMLDivElement>(null);
  const getContainerClassName = useMemo(() => {
    let className = interactionStyles.flipContentContainer;
    if (containerClassName) {
      className += ` ${containerClassName}`;
    }
    return className;
  }, [containerClassName]);

  const CardSide = ({ side }: { side: 0 | 1 }) => {
    if (children.length !== 2) {
      throw new Error(
        'Component: `FlipContent` requires 2 children. Please update the implementation of <FlipContent />'
      );
    }
    const toggleStyles = useMemo(() => getToggleStyles(togglePosition), []);

    const handleFlipAndReturnFocus = (
      e: React.MouseEvent<HTMLElement, MouseEvent>
    ) => {
      // Increase zIndex on flip. Since we are not using css keyframes for animation it's
      // not possible to see the end of animation to clean up z-index.
      // issue visible in case of grid with 3 cards including 1 full size card
      // that's flip horizontally - it's will flip under other cards.
      const zIndexOnFlip = () => {
        if (flipContent.current !== null) {
          flipContent.current.style.zIndex = '1000';
          setTimeout(() => {
            if (flipContent.current !== null) {
              flipContent.current.style.zIndex = cardZIndex;
            }
          }, 600);
        }
      };

      if (isFlipped) {
        zIndexOnFlip();
        handleFlip(e);
        setTimeout(() => {
          if (flipButton.current !== null) {
            flipButton.current.focus();
          }
        }, 250);
        return;
      } else {
        zIndexOnFlip();
        handleFlip(e);
        setTimeout(() => {
          if (backTile.current !== null) {
            backTile.current.focus();
          }
        }, 250);
        return;
      }
    };

    switch (side) {
      case 0:
        // Front of Title – Hide plus icon
        if ($hideIcons) {
          return (
            <>
              {children[side]}
              <Button
                tabIndex={isFlipped ? -1 : 0}
                qeId="flip-button"
                className={`${interactionStyles.buttonReset} ${interactionStyles.flipContentFront} ${interactionStyles.flipContentButton}`}
                ref={flipButton}
                aria-expanded={isFlipped}
                tools={tracking?.frontNoIcon}
                onClick={handleFlipAndReturnFocus}
                $type={Type.Tertiary}
              >
                <span className={interactionStyles.visuallyHidden}>
                  {ariaLabelFront}
                </span>
              </Button>
            </>
          );
        }

        // Front of Tile – Render the plus icon in a div
        return (
          <>
            {children[side]}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                outline: 'none',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                position: 'absolute',
                height: 28,
                width: 28,
                overflow: 'hidden',
                zIndex: 3,
                ...toggleStyles,
              }}
            >
              <Icon
                directory="dls"
                imageName="dls-icon-plus-circle"
                theme={{ color: iconColor }}
                className={interactionStyles.icon}
              />
              <Icon
                directory="dls"
                imageName="dls-icon-plus-circle-filled"
                theme={{ color: iconColor }}
                className={interactionStyles.hoveredIcon}
              />
            </div>
            <Button
              tabIndex={isFlipped ? -1 : 0}
              aria-expanded={false}
              aria-label={ariaLabelFront}
              qeId="flip-button"
              className={`${interactionStyles.buttonReset} ${interactionStyles.flipContentFront} ${interactionStyles.flipContentButton}`}
              ref={flipButton}
              onClick={handleFlipAndReturnFocus}
              tools={tracking?.frontIcon}
              $type={Type.Tertiary}
            >
              <span
                style={{
                  border: '0',
                  clip: 'rect(0 0 0 0)',
                  height: '1px',
                  margin: '-1px',
                  overflow: 'hidden',
                  padding: '0',
                  position: 'absolute',
                  width: '1px',
                }}
              >
                {ariaLabelFront}
              </span>
            </Button>
          </>
        );
      case 1:
        // Back of Tile – Render the minus icon in a clickable button */
        return (
          <div
            className={interactionStyles.backTile}
            tabIndex={-1}
            ref={backTile}
          >
            {children[side]}
            <Button
              tabIndex={isFlipped ? 0 : -1}
              aria-expanded={true}
              qeId="cancel-button"
              className={`${interactionStyles.cancelButton} ${interactionStyles[togglePosition]}`}
              aria-label={ariaLabelBack}
              onClick={handleFlipAndReturnFocus}
              tools={tracking?.backIcon}
              $type={Type.Tertiary}
            >
              <Icon
                directory="dls"
                imageName="dls-icon-cancel-circle"
                theme={{ color: iconColor }}
                className={interactionStyles.icon}
                Component="span"
              />
              <Icon
                directory="dls"
                imageName="dls-icon-cancel-circle-filled"
                theme={{ color: iconColor }}
                className={interactionStyles.hoveredIcon}
                Component="span"
              />
            </Button>
          </div>
        );
    }
  };

  const frontRotateY = `rotateY(${
    isInfinite ? rotation : isFlipped ? 180 : 0
  }deg)`;
  const backRotateY = `translate(-100%, 0) rotateY(${
    isInfinite ? rotation + 180 : isFlipped ? 0 : -180
  }deg)`;
  const frontRotateX = `rotateX(${
    isInfinite ? rotation : isFlipped ? 180 : 0
  }deg)`;
  const backRotateX = `translate(-100%, 0) rotateX(${
    isInfinite ? rotation + 180 : isFlipped ? 0 : -180
  }deg)`;

  const styles: {
    [key: string]: FlipStyles;
  } = {
    back: {
      WebkitBackfaceVisibility: 'hidden',
      backfaceVisibility: 'hidden',
      MozBackfaceVisibility: 'hidden',
      visibility: isFlipped ? 'visible' : 'hidden',
      minHeight: '100%',
      height: 'auto',
      transform:
        flipDirection === FlipDirection.Horizontal ? backRotateY : backRotateX,
      WebkitTransform:
        flipDirection === FlipDirection.Horizontal ? backRotateY : backRotateX,
      MozTransform:
        flipDirection === FlipDirection.Horizontal ? backRotateY : backRotateX,
      transition: `transform ${flipSpeedFrontToBack}s ease-in-out, opacity ${flipSpeedFrontToBack}s ease-in-out, visibility ${flipSpeedFrontToBack}s ease-in-out`,
      width: '100%',
      minWidth: '100%',
      ...backStyles,
    },
    container: {
      zIndex: `${cardZIndex}`,
      position: 'relative',
      perspective: '1000px',
    },
    flipper: {
      display: 'flex',
      height: '100%',
      maxHeight: cardMaxHeight ? cardMaxHeight : 'inherit',
      transformStyle: 'preserve-3d',
    },
    front: {
      WebkitBackfaceVisibility: 'hidden',
      backfaceVisibility: 'hidden',
      MozBackfaceVisibility: 'hidden',
      visibility: isFlipped ? 'hidden' : 'visible',
      minHeight: '100%',
      height: 'auto',
      transform:
        flipDirection === FlipDirection.Horizontal
          ? frontRotateY
          : frontRotateX,
      WebkitTransform:
        flipDirection === FlipDirection.Horizontal
          ? frontRotateY
          : frontRotateX,
      MozTransform:
        flipDirection === FlipDirection.Horizontal
          ? frontRotateY
          : frontRotateX,
      transition: `transform ${flipSpeedBackToFront}s ease-in-out, opacity ${flipSpeedBackToFront}s ease-in-out, visibility ${flipSpeedBackToFront}s ease-in-out`,
      width: '100%',
      minWidth: '100%',
      zIndex: '2',
      ...frontStyles,
    },
  };

  return (
    <div
      className={getContainerClassName}
      ref={flipContent}
      style={{
        ...styles.container,
        ...containerStyle,
      }}
      {...rest}
    >
      <div
        className={`flip-content-flipper ${interactionStyles.flipContentFlipper}`}
        style={styles.flipper}
      >
        <div
          className={`flip-content-front ${interactionStyles.flipContentFront} ${frontClassName}`}
          style={styles.front}
          aria-hidden={isFlipped}
        >
          <CardSide side={0} />
        </div>

        <div
          className={`flip-content-back ${interactionStyles.flipContentBack} ${backClassName}`}
          style={styles.back}
          aria-hidden={!isFlipped}
        >
          <CardSide side={1} />
        </div>
      </div>
    </div>
  );
};
